import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  LOCATION_KINDS,
  LOCATION_KIND_LABELS,
  useCreateLocation,
  useUpdateLocation,
  type Location,
  type LocationKind,
} from '@/features/inventory/hooks';

interface LocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided the dialog edits this location instead of creating one. */
  location?: Location | null;
  onSaved?: (locationId: string) => void;
}

// Radix's <Select> can't carry an empty-string value, so "no kind" is its
// own sentinel token.
const KIND_NONE = 'none';

/**
 * Create / edit modal for the location master. Locations are the physical
 * (or logical) buckets on-hand inventory is tracked against — warehouses,
 * the production floor, transit, and quarantine. A code + name is the
 * minimum; the kind drives how the on-hand grid groups the bucket and the
 * optional address is for shipping / receiving paperwork.
 */
export function LocationDialog({ open, onOpenChange, location, onSaved }: LocationDialogProps) {
  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation();
  const isEdit = !!location;

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<string>(KIND_NONE);
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setCode(location?.code ?? '');
    setName(location?.name ?? '');
    setKind(location?.kind ?? KIND_NONE);
    setLine1(location?.address?.line1 ?? '');
    setLine2(location?.address?.line2 ?? '');
    setCity(location?.address?.city ?? '');
    setState(location?.address?.state ?? '');
    setZip(location?.address?.zip ?? '');
    setIsActive(location?.is_active ?? true);
  }, [open, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedCode = code.trim();
    const trimmedName = name.trim();
    if (!trimmedCode) {
      toast.error('Code is required.');
      return;
    }
    if (!trimmedName) {
      toast.error('Name is required.');
      return;
    }

    const fields = {
      code: trimmedCode,
      name: trimmedName,
      kind: kind === KIND_NONE ? null : (kind as LocationKind),
      address: {
        line1: line1.trim() || null,
        line2: line2.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        zip: zip.trim() || null,
      },
      is_active: isActive,
    };

    try {
      if (isEdit && location) {
        const updated = await updateLocation.mutateAsync({ id: location.id, patch: fields });
        toast.success(`Updated “${updated.name}”`);
        onOpenChange(false);
        onSaved?.(updated.id);
      } else {
        const created = await createLocation.mutateAsync(fields);
        toast.success(`Created “${created.name}”`);
        onOpenChange(false);
        onSaved?.(created.id);
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : `Failed to ${isEdit ? 'update' : 'create'} location.`,
      );
    }
  };

  const submitting = createLocation.isPending || updateLocation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit location' : 'Add location'}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="location-code">
                Code <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="location-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. WH-MAIN"
                maxLength={64}
                className="font-mono text-xs"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="location-name">
                Name <span className="text-fresh-salmon-dark">*</span>
              </Label>
              <Input
                id="location-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Main warehouse"
                maxLength={200}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="location-kind">Kind</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger id="location-kind">
                  <SelectValue placeholder="Select a kind" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={KIND_NONE}>
                    <span className="text-muted-foreground">No kind</span>
                  </SelectItem>
                  {LOCATION_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {LOCATION_KIND_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 sm:mt-7">
              <Label htmlFor="location-active" className="cursor-pointer">
                Active
              </Label>
              <Switch id="location-active" checked={isActive} onCheckedChange={setIsActive} />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="location-line1">Address</Label>
              <Input
                id="location-line1"
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                placeholder="Street address"
                maxLength={300}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="location-line2">Address line 2</Label>
              <Input
                id="location-line2"
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                placeholder="Suite, unit, dock (optional)"
                maxLength={300}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="location-city">City</Label>
              <Input
                id="location-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Portland"
                maxLength={120}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="location-state">State</Label>
                <Input
                  id="location-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="e.g. OR"
                  maxLength={60}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="location-zip">Zip code</Label>
                <Input
                  id="location-zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="e.g. 97201"
                  maxLength={20}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting
                ? isEdit
                  ? 'Saving…'
                  : 'Creating…'
                : isEdit
                  ? 'Save changes'
                  : 'Create location'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
